// Types for the Launchpad feature

export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';
export type TaskPriority = 'low' | 'medium' | 'high' | 'critical';
export type TaskCategory = 
  | 'legal' 
  | 'finance' 
  | 'marketing' 
  | 'operations' 
  | 'product' 
  | 'sales' 
  | 'hr' 
  | 'admin'
  | 'research'
  | 'planning';

export interface LaunchTask {
  id: string;
  user_id: string;
  business_id?: string; // Optional reference to a specific business
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  category: TaskCategory;
  due_date: string; // ISO date string
  scheduled_day: number; // Day number from business start (1-365)
  completed_at?: string; // ISO date string
  notes?: string;
  resources?: string[];
  dependencies?: string[]; // IDs of tasks that must be completed before this one
  partner_recommendations?: PartnerRecommendation[];
  created_at: string;
  updated_at: string;
}

export interface LaunchCalendar {
  id: string;
  user_id: string;
  business_id?: string;
  start_date: string; // When the launch calendar begins
  current_day: number; // Current day in the launch process (1-365)
  last_updated: string;
  progress: number; // Overall progress percentage
  category_progress: Record<TaskCategory, number>; // Progress by category
  created_at: string;
  updated_at: string;
}

export interface LaunchpadState {
  calendar: LaunchCalendar | null;
  tasks: LaunchTask[];
  currentDate: string;
  selectedDate: string;
  isLoading: boolean;
  error: string | null;
}

// Partner recommendation for affiliate marketing
export interface PartnerRecommendation {
  name: string;
  description: string;
  logo_url?: string;
  website_url: string;
  regions?: string[]; // e.g., ['US', 'UK']
  referral_code?: string;
  referral_url?: string;
  commission_info?: string; // e.g., '£50 per new account'
  category: 'banking' | 'accounting' | 'legal' | 'marketing' | 'insurance' | 'tools' | 'other';
}

// Predefined task templates for different business types
export interface TaskTemplate {
  title: string;
  description: string;
  category: TaskCategory;
  priority: TaskPriority;
  scheduled_day: number;
  resources?: string[];
  dependencies?: string[];
  partner_recommendations?: PartnerRecommendation[];
}

// Business launch stages
export enum LaunchStage {
  PLANNING = 'planning',
  SETUP = 'setup',
  DEVELOPMENT = 'development',
  PRE_LAUNCH = 'pre_launch',
  LAUNCH = 'launch',
  POST_LAUNCH = 'post_launch',
  GROWTH = 'growth'
}

// Helper function to determine the stage based on the day
export const getDayStage = (day: number): LaunchStage => {
  if (day <= 30) return LaunchStage.PLANNING;
  if (day <= 60) return LaunchStage.SETUP;
  if (day <= 120) return LaunchStage.DEVELOPMENT;
  if (day <= 150) return LaunchStage.PRE_LAUNCH;
  if (day <= 180) return LaunchStage.LAUNCH;
  if (day <= 270) return LaunchStage.POST_LAUNCH;
  return LaunchStage.GROWTH;
};

// Helper function to get stage color
export const getStageColor = (stage: LaunchStage): string => {
  switch (stage) {
    case LaunchStage.PLANNING: return 'blue';
    case LaunchStage.SETUP: return 'purple';
    case LaunchStage.DEVELOPMENT: return 'indigo';
    case LaunchStage.PRE_LAUNCH: return 'orange';
    case LaunchStage.LAUNCH: return 'red';
    case LaunchStage.POST_LAUNCH: return 'green';
    case LaunchStage.GROWTH: return 'teal';
    default: return 'gray';
  }
};
